import { LogServices } from "../services";
import { GraylogModel } from "../models/graylog";
import { LOCALSTORAGE_AUTH2, LOCALSTORAGE_USERSSION } from "../common/constants";

const graylogService = new LogServices();

/**
 * Log Categories for filtering in Graylog
 */
export enum GraylogCategory {
    WEBVIEW = 'WEBVIEW',
    VOIP = 'VOIP',
    EMAIL = 'EMAIL',
    CONFIG = 'CONFIG',
    EXTERNAL_SERVICE = 'EXTERNAL_SERVICE',
    IFRAME = 'IFRAME',
    NOTIFICATION = 'NOTIFICATION',
    PROPERTY = 'PROPERTY',
    REPORT = 'REPORT',
    DOCUMENT_AI = 'DOCUMENT_AI'
}

/**
 * Log Actions for filtering in Graylog
 */
export enum GraylogAction {
    // Message actions
    MESSAGE_RECEIVED = 'MESSAGE_RECEIVED',
    MESSAGE_SENT = 'MESSAGE_SENT',

    // API actions
    API_CALL_START = 'API_CALL_START',
    API_CALL_SUCCESS = 'API_CALL_SUCCESS',
    API_CALL_ERROR = 'API_CALL_ERROR',

    // General actions
    ACTION_START = 'ACTION_START',
    ACTION_SUCCESS = 'ACTION_SUCCESS',
    ACTION_ERROR = 'ACTION_ERROR',

    // Config actions
    CONFIG_CHANGE = 'CONFIG_CHANGE',
    CONFIG_REGISTER = 'CONFIG_REGISTER',
    CONFIG_UNREGISTER = 'CONFIG_UNREGISTER'
}

/**
 * Log Levels for use in the application (Syslog standard naming)
 * These are mapped to Serilog levels before sending to backend
 */
export enum GraylogLevel {
    EMERGENCY = 0,
    ALERT = 1,
    CRITICAL = 2,
    ERROR = 3,
    WARNING = 4,
    NOTICE = 5,
    INFO = 6,
    DEBUG = 7
}

/**
 * Serilog Log Levels (used by backend)
 * These must match Serilog.Events.LogEventLevel enum in C#
 */
enum SerilogLevel {
    Verbose = 0,
    Debug = 1,
    Information = 2,
    Warning = 3,
    Error = 4,
    Fatal = 5
}

/**
 * Map GraylogLevel (Syslog) to SerilogLevel (backend)
 * Backend only handles Information, Warning, and Error levels
 */
const mapToSerilogLevel = (level: GraylogLevel): SerilogLevel => {
    switch (level) {
        case GraylogLevel.EMERGENCY:
        case GraylogLevel.ALERT:
        case GraylogLevel.CRITICAL:
        case GraylogLevel.ERROR:
            return SerilogLevel.Error;
        case GraylogLevel.WARNING:
            return SerilogLevel.Warning;
        case GraylogLevel.NOTICE:
        case GraylogLevel.INFO:
            return SerilogLevel.Information;
        case GraylogLevel.DEBUG:
            return SerilogLevel.Debug;
        default:
            return SerilogLevel.Information;
    }
};

/**
 * Interface for standardized log options
 */
export interface GraylogLogOptions {
    category: GraylogCategory;
    serviceName: string;
    action: GraylogAction | string;
    level?: GraylogLevel;
    detail?: any;
    startTime?: string;
    endTime?: string;
    duration?: string;
    sessionId?: string;
}

/**
 * Get auth data from localStorage
 * - customerName and customerKey from LOCALSTORAGE_AUTH2 (AuthorizationData)
 * - userId from LOCALSTORAGE_USERSSION (userssion) which contains UserInfo.Id
 */
const getAuthData = () => {
    try {
        // Get customer info from AuthorizationData
        let authData = localStorage.getItem(LOCALSTORAGE_AUTH2) as any;
        authData = authData ? JSON.parse(authData) : {};

        // Get user info from userssion (contains UserInfo.Id)
        let userSession = localStorage.getItem(LOCALSTORAGE_USERSSION) as any;
        userSession = userSession ? JSON.parse(userSession) : {};

        return {
            customerName: authData?.data?.information?.customerName || 'Unknown',
            userId: userSession?.UserInfo?.Id || 0,
            customerKey: authData?.data?.information?.key || authData?.key || 'Unknown'
        };
    } catch {
        return { customerName: 'Unknown', userId: 0, customerKey: 'Unknown' };
    }
};

/**
 * Calculate duration between two timestamps
 */
export const calculateDuration = (startTime: Date, endTime: Date = new Date()): string => {
    const duration = endTime.getTime() - startTime.getTime();
    return new Date(duration).toISOString().substr(11, 12);
};

/**
 * Main logging function with standardized format
 */
export const graylogLog = (options: GraylogLogOptions): void => {
    try {
        const { customerName, userId, customerKey } = getAuthData();
        const {
            category,
            serviceName,
            action,
            level = GraylogLevel.INFO,
            detail = null,
            startTime,
            endTime,
            duration,
            sessionId
        } = options;

        const message = `[${category}] [${serviceName}] ${action}`;

        // Map Syslog level to Serilog level for backend compatibility
        const serilogLevel = mapToSerilogLevel(level);

        const logData = new GraylogModel({
            LogLevel: serilogLevel,
            Message: message,
            Data: {
                customer: customerName,
                userId: userId,
                action: action,
                startTime: startTime,
                endTime: endTime,
                duration: duration,
                detail: {
                    category: category,
                    serviceName: serviceName,
                    customerKey: customerKey,
                    customerName: customerName,
                    sessionId: sessionId || null,
                    ...detail
                }
            }
        });

        graylogService.logMessage(logData).then(() => {
            console.log(`[Graylog] ${message}`);
        }).catch(ex => {
            console.error('[Graylog] Failed to log:', ex);
        });
    } catch (error) {
        console.error('[Graylog] Error logging:', error);
    }
};

/**
 * Log with timing - automatically calculates duration
 */
export const graylogLogWithTiming = (
    options: Omit<GraylogLogOptions, 'startTime' | 'endTime' | 'duration'>,
    startTime: Date
): void => {
    const endTime = new Date();
    graylogLog({
        ...options,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        duration: calculateDuration(startTime, endTime)
    });
};

/**
 * Shorthand for WEBVIEW category logs
 */
export const graylogWebview = (
    serviceName: string,
    action: GraylogAction | string,
    detail?: any
): void => {
    graylogLog({
        category: GraylogCategory.WEBVIEW,
        serviceName,
        action,
        detail
    });
};

/**
 * Shorthand for IFRAME category logs
 */
export const graylogIframe = (
    serviceName: string,
    action: GraylogAction | string,
    iframeName: string,
    detail?: any
): void => {
    graylogLog({
        category: GraylogCategory.IFRAME,
        serviceName,
        action,
        detail: {
            iframeName,
            ...detail
        }
    });
};

/**
 * Shorthand for VOIP category logs
 */
export const graylogVoip = (
    serviceName: string,
    action: GraylogAction | string,
    detail?: any,
    startTime?: Date
): void => {
    if (startTime) {
        graylogLogWithTiming({
            category: GraylogCategory.VOIP,
            serviceName,
            action,
            detail
        }, startTime);
    } else {
        graylogLog({
            category: GraylogCategory.VOIP,
            serviceName,
            action,
            detail
        });
    }
};

/**
 * Shorthand for EMAIL/REPORT category logs
 */
export const graylogEmail = (
    serviceName: string,
    action: GraylogAction | string,
    detail?: any,
    startTime?: Date
): void => {
    if (startTime) {
        graylogLogWithTiming({
            category: GraylogCategory.EMAIL,
            serviceName,
            action,
            detail
        }, startTime);
    } else {
        graylogLog({
            category: GraylogCategory.EMAIL,
            serviceName,
            action,
            detail
        });
    }
};

/**
 * Shorthand for CONFIG category logs
 */
export const graylogConfig = (
    serviceName: string,
    action: GraylogAction | string,
    detail?: any
): void => {
    graylogLog({
        category: GraylogCategory.CONFIG,
        serviceName,
        action,
        detail
    });
};

/**
 * Shorthand for EXTERNAL_SERVICE category logs
 */
export const graylogExternalService = (
    serviceName: string,
    action: GraylogAction | string,
    detail?: any
): void => {
    graylogLog({
        category: GraylogCategory.EXTERNAL_SERVICE,
        serviceName,
        action,
        detail
    });
};

/**
 * Log error with ERROR level
 */
export const graylogError = (
    category: GraylogCategory,
    serviceName: string,
    error: any,
    detail?: any
): void => {
    graylogLog({
        category,
        serviceName,
        action: GraylogAction.ACTION_ERROR,
        level: GraylogLevel.ERROR,
        detail: {
            error: error?.message || error,
            stack: error?.stack,
            ...detail
        }
    });
};

// ============================================
// AI IMAGE FLOW LOGGING
// ============================================

/**
 * AI Image Flow Steps for tracking the complete flow
 */
export enum AIImageFlowStep {
    // WINAPP -> WEBAPP
    WINAPP_MESSAGE_RECEIVED = 'WINAPP_MESSAGE_RECEIVED',

    // WEBAPP Processing
    AUTH_START = 'AUTH_START',
    AUTH_SUCCESS = 'AUTH_SUCCESS',
    AUTH_ERROR = 'AUTH_ERROR',
    IFRAME_LOADING = 'IFRAME_LOADING',
    IFRAME_LOADED = 'IFRAME_LOADED',
    IFRAME_ERROR = 'IFRAME_ERROR',

    // WEBAPP -> WINAPP
    WINAPP_COMPLETE_LOADING = 'WINAPP_COMPLETE_LOADING',

    // IFRAME -> WEBAPP
    IFRAME_MESSAGE_RECEIVED = 'IFRAME_MESSAGE_RECEIVED',

    // WEBAPP -> WINAPP (forwarding iframe events)
    WINAPP_CANCEL = 'WINAPP_CANCEL',
    WINAPP_SAVE = 'WINAPP_SAVE',
    WINAPP_REPLACE = 'WINAPP_REPLACE',

    // Session persistence (minimize / restore)
    SESSION_MINIMIZE = 'SESSION_MINIMIZE',
    SESSION_SAVE = 'SESSION_SAVE',
    SESSION_RESTORE = 'SESSION_RESTORE',
    SESSION_INIT_BULK = 'SESSION_INIT_BULK',

    // Flow completion
    FLOW_COMPLETED = 'FLOW_COMPLETED',
    FLOW_ERROR = 'FLOW_ERROR'
}

/**
 * Generate unique session ID for AI Image flow tracking
 */
export const generateAIImageSessionId = (): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `ai-img-${timestamp}-${random}`;
};

/**
 * AI Image Flow Logger - specialized for tracking the complete AI Image flow
 *
 * Flow: WINAPP -> WEBAPP (WEBVIEW) -> AI IMAGE (Iframe) -> WEBAPP -> WINAPP
 *
 * @example
 * // At the start of flow, generate sessionId
 * const sessionId = generateAIImageSessionId();
 *
 * // Log each step with the same sessionId
 * graylogAIImageFlow(sessionId, AIImageFlowStep.WINAPP_MESSAGE_RECEIVED, { imageUrl, language });
 * graylogAIImageFlow(sessionId, AIImageFlowStep.AUTH_START);
 * graylogAIImageFlow(sessionId, AIImageFlowStep.IFRAME_LOADED);
 * graylogAIImageFlow(sessionId, AIImageFlowStep.IFRAME_MESSAGE_RECEIVED, { action: 'save' });
 * graylogAIImageFlow(sessionId, AIImageFlowStep.WINAPP_SAVE, { imageName });
 */
export const graylogAIImageFlow = (
    sessionId: string,
    step: AIImageFlowStep,
    detail?: any,
    error?: any
): void => {
    const isError = step === AIImageFlowStep.AUTH_ERROR ||
                    step === AIImageFlowStep.IFRAME_ERROR ||
                    step === AIImageFlowStep.FLOW_ERROR;

    graylogLog({
        category: GraylogCategory.WEBVIEW,
        serviceName: 'AIImageFlow',
        action: step,
        level: isError ? GraylogLevel.ERROR : GraylogLevel.INFO,
        sessionId: sessionId,
        detail: {
            flowSessionId: sessionId,
            step: step,
            error: error ? (error?.message || error) : undefined,
            ...detail
        }
    });
};

/**
 * Create AI Image Flow Logger instance for a session
 * This provides a convenient way to log multiple steps with the same sessionId
 *
 * @example
 * const flowLogger = createAIImageFlowLogger();
 * flowLogger.log(AIImageFlowStep.WINAPP_MESSAGE_RECEIVED, { imageUrl });
 * flowLogger.log(AIImageFlowStep.AUTH_START);
 * flowLogger.log(AIImageFlowStep.IFRAME_LOADED);
 * flowLogger.error(AIImageFlowStep.FLOW_ERROR, someError);
 *
 * // Get sessionId for debugging
 * console.log('Session ID:', flowLogger.sessionId);
 */
export const createAIImageFlowLogger = (existingSessionId?: string) => {
    const sessionId = existingSessionId || generateAIImageSessionId();
    const startTime = new Date();

    return {
        sessionId,
        startTime,

        log: (step: AIImageFlowStep, detail?: any) => {
            graylogAIImageFlow(sessionId, step, {
                ...detail,
                elapsedMs: Date.now() - startTime.getTime()
            });
        },

        error: (step: AIImageFlowStep, error: any, detail?: any) => {
            graylogAIImageFlow(sessionId, step, {
                ...detail,
                elapsedMs: Date.now() - startTime.getTime()
            }, error);
        }
    };
};

// ============================================
// DOCUMENT AI FLOW LOGGING
// ============================================

/**
 * Document AI Flow Steps for tracking the complete flow
 */
export enum DocumentAIFlowStep {
    // WINAPP -> WEBAPP
    WINAPP_MESSAGE_RECEIVED = 'WINAPP_MESSAGE_RECEIVED',

    // WEBAPP Processing
    AUTH_START = 'AUTH_START',
    AUTH_SUCCESS = 'AUTH_SUCCESS',
    AUTH_ERROR = 'AUTH_ERROR',
    IFRAME_LOADING = 'IFRAME_LOADING',
    IFRAME_LOADED = 'IFRAME_LOADED',
    IFRAME_ERROR = 'IFRAME_ERROR',

    // WEBAPP -> WINAPP
    WINAPP_COMPLETE_LOADING = 'WINAPP_COMPLETE_LOADING',

    // IFRAME -> WEBAPP
    IFRAME_MESSAGE_RECEIVED = 'IFRAME_MESSAGE_RECEIVED',

    // WEBAPP -> WINAPP (forwarding iframe events)
    WINAPP_CANCEL = 'WINAPP_CANCEL',
    WINAPP_SAVE = 'WINAPP_SAVE',

    // Flow completion
    FLOW_COMPLETED = 'FLOW_COMPLETED',
    FLOW_ERROR = 'FLOW_ERROR'
}

/**
 * Generate unique session ID for Document AI flow tracking
 */
export const generateDocumentAISessionId = (): string => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `doc-ai-${timestamp}-${random}`;
};

/**
 * Document AI Flow Logger - specialized for tracking the complete Document AI flow
 *
 * Flow: WINAPP -> WEBAPP (WEBVIEW) -> DOCUMENT AI (Iframe) -> WEBAPP -> WINAPP
 */
export const graylogDocumentAIFlow = (
    sessionId: string,
    step: DocumentAIFlowStep,
    detail?: any,
    error?: any
): void => {
    const isError = step === DocumentAIFlowStep.AUTH_ERROR ||
                    step === DocumentAIFlowStep.IFRAME_ERROR ||
                    step === DocumentAIFlowStep.FLOW_ERROR;

    graylogLog({
        category: GraylogCategory.DOCUMENT_AI,
        serviceName: 'DocumentAIFlow',
        action: step,
        level: isError ? GraylogLevel.ERROR : GraylogLevel.INFO,
        sessionId: sessionId,
        detail: {
            flowSessionId: sessionId,
            step: step,
            error: error ? (error?.message || error) : undefined,
            ...detail
        }
    });
};

export interface DocumentAIFlowLogger {
    sessionId: string;
    startTime: Date;
    log: (step: DocumentAIFlowStep, detail?: Record<string, unknown>) => void;
    error: (step: DocumentAIFlowStep, error: unknown, detail?: Record<string, unknown>) => void;
}

/**
 * Create Document AI Flow Logger instance for a session
 */
export const createDocumentAIFlowLogger = (existingSessionId?: string): DocumentAIFlowLogger => {
    const sessionId = existingSessionId || generateDocumentAISessionId();
    const startTime = new Date();

    return {
        sessionId,
        startTime,

        log: (step: DocumentAIFlowStep, detail?: any) => {
            graylogDocumentAIFlow(sessionId, step, {
                ...detail,
                elapsedMs: Date.now() - startTime.getTime()
            });
        },

        error: (step: DocumentAIFlowStep, error: any, detail?: any) => {
            graylogDocumentAIFlow(sessionId, step, {
                ...detail,
                elapsedMs: Date.now() - startTime.getTime()
            }, error);
        }
    };
};
